import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { apiClient } from '@/shared/api/apiClient'
import type { OrganizationAuditLog } from '@/features/organization-users/types/audit-log'
import type { OrganizationUser } from '@/features/organization-users/types/organization-user'
import {
  organizationAuditLogsQueryKey,
  organizationRolesQueryKey,
  organizationUsersQueryKey,
} from '@/features/organization-users/hooks/organization-users-query-keys'

export type OrganizationUserInvitePayload = {
  firstName: string
  lastName: string
  email: string
  roleSlug: string
}

export function useOrganizationUsers(organizationId: string) {
  return useQuery<OrganizationUser[]>({
    queryKey: organizationUsersQueryKey(organizationId),
    queryFn: async () => {
      const response = await apiClient.get<OrganizationUser[]>(
        `/v1/organizations/${organizationId}/users`
      )
      return response.data
    },
  })
}

export function useOrganizationUserRoles(organizationId: string) {
  return useQuery<string[]>({
    queryKey: organizationRolesQueryKey(organizationId),
    gcTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const response = await apiClient.get<string[]>(
        `/v1/organizations/${organizationId}/roles`
      )
      return response.data
    },
  })
}

export function useOrganizationAuditLogs(organizationId: string) {
  return useQuery<OrganizationAuditLog[]>({
    queryKey: organizationAuditLogsQueryKey(organizationId),
    queryFn: async () => {
      const response = await apiClient.get<OrganizationAuditLog[]>(
        `/v1/organizations/${organizationId}/audit-logs`
      )
      return response.data
    },
  })
}

export function useInviteOrganizationUser(organizationId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, OrganizationUserInvitePayload>({
    mutationFn: async (payload: OrganizationUserInvitePayload) => {
      try {
        const response = await apiClient.post(
          `/v1/organizations/${organizationId}/users`,
          payload
        )
        return response.data
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 409) {
            throw new Error('CONFLICT')
          }
        }
        throw error
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationUsersQueryKey(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: organizationAuditLogsQueryKey(organizationId),
      })
    },
  })
}

export function useUpdateUserRole(organizationId: string, userId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, { roleSlug: string }>({
    mutationFn: async (payload: { roleSlug: string }) => {
      const response = await apiClient.put(
        `/v1/organizations/${organizationId}/users/${userId}/roles`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationUsersQueryKey(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: organizationAuditLogsQueryKey(organizationId),
      })
    },
  })
}

export function useDeleteUserRole(organizationId: string, userId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, { roleSlug: string }>({
    mutationFn: async (payload: { roleSlug: string }) => {
      const response = await apiClient.delete(
        `/v1/organizations/${organizationId}/users/${userId}/roles`,
        { data: payload }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationUsersQueryKey(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: organizationAuditLogsQueryKey(organizationId),
      })
    },
  })
}

export function useDeleteUser(organizationId: string) {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, string>({
    mutationFn: async (userId: string) => {
      const response = await apiClient.delete(`/v1/user/${userId}`)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationUsersQueryKey(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: organizationAuditLogsQueryKey(organizationId),
      })
    },
  })
}
